import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { STATUSES, labelFor } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { PhoneIncoming, PhoneOutgoing, PhoneX, MagnifyingGlass, ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePaginatedLeads from "../lib/usePaginatedLeads";

const CALL_TYPES = [
  { id: "incoming", label: "Incoming", icon: PhoneIncoming, color: "#008A00" },
  { id: "outgoing", label: "Outgoing", icon: PhoneOutgoing, color: "#002FA7" },
  { id: "missed", label: "Missed", icon: PhoneX, color: "#E00000" },
];

const callTypeMeta = (t) => CALL_TYPES.find((c) => c.id === t) || { label: t || "—", icon: PhoneIncoming, color: "#52525B" };

const fmtDuration = (s) => {
  const n = Number(s) || 0;
  if (n <= 0) return "—";
  const m = Math.floor(n / 60);
  const r = n % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
};

export default function Callyzer() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const initialFilter = {
    call_type: "", agent_id: "", search: "", date_from: "", date_to: "",
    phone: "", min_duration: "", max_duration: "",
  };
  const [filter, setFilter] = useState(initialFilter);

  const agentMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.name])), [agents]);

  // Use server-side filtering (push everything to the API for consistency with the export)
  const buildParams = () => {
    const params = { source: "callyzer" };
    if (filter.search) params.search = filter.search;
    if (filter.agent_id) params.agent_id = filter.agent_id;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    if (filter.phone) params.phone = filter.phone;
    if (filter.call_type) params.call_type = filter.call_type;
    if (filter.min_duration !== "" && filter.min_duration != null) params.min_duration = filter.min_duration;
    if (filter.max_duration !== "" && filter.max_duration != null) params.max_duration = filter.max_duration;
    return params;
  };

  const {
    leads, total, loading: leadsLoading, hasMore, isLoadingMore, loadMore,
    reload: reloadLeads,
  } = usePaginatedLeads(buildParams, [JSON.stringify(filter)]);

  const loading = leadsLoading || agentsLoading;

  const loadAgents = async () => {
    setAgentsLoading(true);
    try {
      const ag = agents.length ? { data: agents } : await api.get("/agents");
      setAgents(ag.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setAgentsLoading(false);
  };

  const load = async () => {
    await Promise.all([loadAgents(), reloadLeads()]);
  };

  const exportCsv = async (includeFilters = true) => {
    const params = new URLSearchParams();
    if (includeFilters) {
      Object.entries(buildParams()).forEach(([k, v]) => params.append(k, String(v)));
    } else {
      params.append("source", "callyzer");
    }
    try {
      const res = await api.get(`/leads/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `callyzer_${includeFilters ? "filtered" : "all"}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(includeFilters ? "Filtered CSV downloaded" : "All Callyzer leads downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { loadAgents(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => {
    const c = { incoming: 0, outgoing: 0, missed: 0, total: leads.length };
    for (const l of leads) {
      const t = l.call_type;
      if (t in c) c[t] += 1;
    }
    return c;
  }, [leads]);

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Telephony"
        title="Callyzer"
        description="Every incoming, outgoing and missed call from Callyzer — auto-ingested as leads."
        actions={
          <>
            <button onClick={() => exportCsv(true)} data-testid="callyzer-export-filtered"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
              <DownloadSimple size={16} /> Export filtered
            </button>
            <button onClick={() => exportCsv(false)} data-testid="callyzer-export-all"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors">
              <DownloadSimple size={16} /> Export all
            </button>
            <button
              onClick={load}
              data-testid="callyzer-refresh"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors"
            >
              <ArrowsClockwise size={16} /> Refresh
            </button>
          </>
        }
      />

      {/* KPI strip */}
      <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="callyzer-kpis">
        <KpiCard testid="kpi-total" label="Total calls" value={total} color="#002FA7" />
        <KpiCard testid="kpi-incoming" label="Incoming" value={counts.incoming} color="#008A00" icon={PhoneIncoming} />
        <KpiCard testid="kpi-outgoing" label="Outgoing" value={counts.outgoing} color="#002FA7" icon={PhoneOutgoing} />
        <KpiCard testid="kpi-missed" label="Missed" value={counts.missed} color="#E00000" icon={PhoneX} />
      </div>

      {/* Filters */}
      <div className="px-8 py-4 flex flex-wrap gap-2 items-center">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search name or number"
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            data-testid="callyzer-search"
            className="text-sm border border-border pl-9 pr-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-64"
          />
        </div>
        <select
          value={filter.call_type}
          onChange={(e) => setFilter({ ...filter, call_type: e.target.value })}
          data-testid="callyzer-filter-type"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All call types</option>
          {CALL_TYPES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        {isAdmin && (
          <select
            value={filter.agent_id}
            onChange={(e) => setFilter({ ...filter, agent_id: e.target.value })}
            data-testid="callyzer-filter-agent"
            className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
          >
            <option value="">All agents</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <input
          type="date"
          value={filter.date_from}
          onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
          data-testid="callyzer-filter-from"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        />
        <span className="text-muted-foreground text-xs">→</span>
        <input
          type="date"
          value={filter.date_to}
          onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
          data-testid="callyzer-filter-to"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        />
        <input
          placeholder="Phone contains"
          value={filter.phone}
          onChange={(e) => setFilter({ ...filter, phone: e.target.value })}
          data-testid="callyzer-filter-phone"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-36"
        />
        <input
          type="number" min={0}
          placeholder="Min sec"
          value={filter.min_duration}
          onChange={(e) => setFilter({ ...filter, min_duration: e.target.value })}
          data-testid="callyzer-filter-min-duration"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-24"
        />
        <input
          type="number" min={0}
          placeholder="Max sec"
          value={filter.max_duration}
          onChange={(e) => setFilter({ ...filter, max_duration: e.target.value })}
          data-testid="callyzer-filter-max-duration"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-24"
        />
        {Object.values(filter).some((v) => v !== "" && v !== null && v !== undefined) && (
          <button
            onClick={() => setFilter(initialFilter)}
            data-testid="callyzer-filter-clear"
            className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid="callyzer-count">
          {leads.length.toLocaleString()} of {total.toLocaleString()} call{total !== 1 && "s"}
        </span>
      </div>

      {/* Table */}
      <div className="px-8 pb-8">
        <div className="border border-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold w-32">Call</th>
                <th className="px-4 py-3 font-bold">Customer</th>
                <th className="px-4 py-3 font-bold">Phone</th>
                <th className="px-4 py-3 font-bold">Agent</th>
                <th className="px-4 py-3 font-bold">Duration</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">When</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center">
                  <div className="text-sm text-muted-foreground mb-2">No Callyzer calls yet.</div>
                  <div className="text-xs text-muted-foreground">
                    Configure your Callyzer webhook in <Link to="/webhooks" className="text-primary hover:underline">Webhooks</Link> to start ingesting calls.
                  </div>
                </td></tr>
              ) : leads.map((l) => {
                const meta = callTypeMeta(l.call_type);
                const Icon = meta.icon;
                return (
                  <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`callyzer-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2" style={{ color: meta.color }}>
                        <Icon size={16} weight="bold" />
                        <span className="text-xs uppercase tracking-[0.1em] font-bold">{meta.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/leads/${l.id}`} className="hover:text-primary hover:underline" data-testid={`callyzer-lead-link-${l.id}`}>
                        {l.customer_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">{l.phone || "—"}</td>
                    <td className="px-4 py-3">{agentMap[l.agent_id] || "—"}</td>
                    <td className="px-4 py-3 tabular-nums">{fmtDuration(l.call_duration)}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums text-xs">
                      {l.call_date || l.call_time
                        ? `${l.call_date || ""} ${l.call_time || ""}`.trim()
                        : new Date(l.created_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="mt-3 flex items-center justify-center">
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              data-testid="callyzer-load-more"
              className="px-6 py-2.5 border-2 border-primary text-primary text-xs uppercase tracking-[0.15em] font-bold hover:bg-primary hover:text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoadingMore
                ? "Loading…"
                : `Load more · ${(total - leads.length).toLocaleString()} remaining`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ testid, label, value, color, icon: Icon }) {
  return (
    <div className="border border-border bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
        {Icon && <Icon size={16} weight="bold" style={{ color }} />}
      </div>
      <div className="font-display text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
